import React from 'react';
import { FolderSearch, Inbox, Layers, Send, User, Users, type LucideIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { normalizeSearchFolderKind, type SearchFolderKind } from '../../search-folders/useData';

export type SearchFolderSidebarAccent = SearchFolderKind;

type AccentTokens = {
  iconDefault: string;
  iconSelected: string;
  borderSelected: string;
  hoverRow: string;
};

const SEARCH_FOLDER_ACCENTS: Record<SearchFolderSidebarAccent, AccentTokens> = {
  search: {
    iconDefault: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-950/55 dark:text-indigo-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-indigo-400',
    hoverRow: 'hover:bg-indigo-50/70 dark:hover:bg-indigo-950/25',
  },
  inbox: {
    iconDefault: 'bg-sky-100 text-sky-600 dark:bg-sky-950/55 dark:text-sky-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-sky-400',
    hoverRow: 'hover:bg-sky-50/70 dark:hover:bg-sky-950/25',
  },
  combined: {
    iconDefault: 'bg-violet-100 text-violet-600 dark:bg-violet-950/55 dark:text-violet-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-violet-400',
    hoverRow: 'hover:bg-violet-50/70 dark:hover:bg-violet-950/25',
  },
  sent: {
    iconDefault: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/55 dark:text-emerald-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-emerald-400',
    hoverRow: 'hover:bg-emerald-50/70 dark:hover:bg-emerald-950/25',
  },
  combinedSent: {
    iconDefault: 'bg-teal-100 text-teal-600 dark:bg-teal-950/55 dark:text-teal-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-teal-400',
    hoverRow: 'hover:bg-teal-50/70 dark:hover:bg-teal-950/25',
  },
  mySent: {
    iconDefault: 'bg-teal-100 text-teal-600 dark:bg-teal-950/55 dark:text-teal-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-teal-400',
    hoverRow: 'hover:bg-teal-50/70 dark:hover:bg-teal-950/25',
  },
  combinedMySent: {
    iconDefault: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-950/55 dark:text-cyan-400',
    iconSelected: 'bg-white/20 text-white ring-1 ring-white/25',
    borderSelected: 'border-l-cyan-400',
    hoverRow: 'hover:bg-cyan-50/70 dark:hover:bg-cyan-950/25',
  },
};

export function accentFromSearchFolderKind(kind?: string | null): SearchFolderSidebarAccent {
  return normalizeSearchFolderKind(kind);
}

export function searchFolderKindLucideIcon(kind?: string | null): LucideIcon {
  switch (normalizeSearchFolderKind(kind)) {
    case 'inbox':
      return Inbox;
    case 'combined':
      return Layers;
    case 'sent':
      return Send;
    case 'combinedSent':
      return Layers;
    case 'mySent':
      return User;
    case 'combinedMySent':
      return Users;
    default:
      return FolderSearch;
  }
}

export function searchFolderRowSelected(accent: SearchFolderSidebarAccent, rowSelectedBase: string): string {
  return cn(rowSelectedBase, SEARCH_FOLDER_ACCENTS[accent].borderSelected);
}

export function searchFolderRowDefault(accent: SearchFolderSidebarAccent, rowDefault: string): string {
  return cn(rowDefault, SEARCH_FOLDER_ACCENTS[accent].hoverRow);
}

export function SearchFolderSidebarIcon({
  kind,
  selected,
  children,
}: {
  kind?: string | null;
  selected: boolean;
  children?: React.ReactNode;
}) {
  const accent = accentFromSearchFolderKind(kind);
  const tokens = SEARCH_FOLDER_ACCENTS[accent];
  const Icon = searchFolderKindLucideIcon(kind);

  return (
    <span
      className={cn(
        'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition-colors [&_svg]:h-4 [&_svg]:w-4',
        selected ? tokens.iconSelected : tokens.iconDefault,
      )}
      aria-hidden
    >
      {children ?? <Icon className="h-4 w-4" aria-hidden />}
    </span>
  );
}
